import { PinnedNavItem } from '@certhon/user-settings';
import {
  ArrowDown,
  ArrowUp,
  Home,
  Pin,
  PinOff,
  type LucideIcon,
} from 'lucide-react';
import { ReactElement } from 'react';

import {
  SidebarMenuAction,
  SidebarMenuSubAction,
} from '@/components/ui/Sidebar/sidebar';
import { UnifiedContextMenu } from '@/components/ui/UnifiedContextMenu';
import { UnifiedAction } from '@/components/ui/UnifiedContextMenu/types';
import { usePinnedNav } from '@/features/pinned-nav/pinned-nav.hooks';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

/**
 * Washed-out styling for the row actions (pin, drag handle).
 *
 * The peer-variant overrides matter: SidebarMenuAction brightens itself to the
 * accent foreground while the row is hovered or active — which is exactly when
 * the action is visible — so a plain `text-muted-foreground` never applies.
 */
export const navRowActionClass = `text-muted-foreground/50 hover:text-muted-foreground
  hover:bg-transparent peer-hover/menu-button:text-muted-foreground/50
  peer-data-[active=true]/menu-button:text-muted-foreground/50`;

/**
 * The pin toggle shown on every navigation row: washed out and only revealed
 * on hover, so a menu of them does not compete with the labels.
 *
 * Desktop only — on touch there is no hover to reveal it with, and the
 * long-press menu already offers pinning.
 */
export function PinToggleAction({
  item,
  sub = false,
  className,
}: {
  item: PinnedNavItem;
  /** Render for a sub-menu row (shorter, different hover group). */
  sub?: boolean;
  className?: string;
}) {
  const isMobile = useIsMobile();
  const { isPinned, togglePin, canEdit } = usePinnedNav();
  const pinned = isPinned(item.url);
  const Icon: LucideIcon = pinned ? PinOff : Pin;
  const action = pinned ? `Unpin ${item.title}` : `Pin ${item.title}`;
  const label = canEdit ? action : `${action} (offline)`;
  const Action = sub ? SidebarMenuSubAction : SidebarMenuAction;

  if (isMobile) return null;

  return (
    <Action
      showOnHover
      // Pins live in the user's server-side settings, which cannot be written
      // offline; the click would be undone the moment the request failed.
      disabled={!canEdit}
      title={label}
      aria-label={label}
      onClick={(event) => {
        // The row is a link; a pin click must not navigate.
        event.preventDefault();
        event.stopPropagation();
        togglePin(item);
      }}
      className={cn(navRowActionClass, className)}
    >
      <Icon />
    </Action>
  );
}

/**
 * Wraps a navigation row in its action menu: right-click on desktop, long-press
 * on touch.
 *
 * The rows are links, so the browser's own menu still has to be reachable —
 * hold NATIVE_MENU_MODIFIER while right-clicking for "open in new tab" and
 * friends.
 */
export function NavRowContextMenu({
  item,
  children,
  pinnedIndex,
  pinnedCount,
  extraActions,
}: {
  item: PinnedNavItem;
  children: ReactElement;
  /** Position in the pinned list, when this row *is* a pin. */
  pinnedIndex?: number;
  pinnedCount?: number;
  /** Row-specific entries, appended below the pin actions. */
  extraActions?: UnifiedAction<PinnedNavItem>[];
}) {
  const { isPinned, togglePin, movePin, makeHome, canEdit } = usePinnedNav();

  const pinned = isPinned(item.url);
  const isPin = pinnedIndex !== undefined && pinnedCount !== undefined;
  // Every action here writes the user's server-side settings, which has no
  // offline queue — greyed out beats a change that disappears.
  const offlineNote = canEdit ? '' : ' (offline)';

  const actions: UnifiedAction<PinnedNavItem>[] = [
    {
      label: (pinned ? 'Unpin' : 'Pin to top') + offlineNote,
      icon: pinned ? PinOff : Pin,
      disabled: !canEdit,
      onAction: () => togglePin(item),
    },
  ];

  if (isPin) {
    actions.push(
      { type: 'separator' },
      {
        label: 'Make home page',
        icon: Home,
        disabled: !canEdit || pinnedIndex === 0,
        onAction: () => makeHome(item.url),
      },
      {
        label: 'Move up',
        icon: ArrowUp,
        disabled: !canEdit || pinnedIndex === 0,
        onAction: () => movePin(item.url, -1),
      },
      {
        label: 'Move down',
        icon: ArrowDown,
        disabled: !canEdit || pinnedIndex === pinnedCount - 1,
        onAction: () => movePin(item.url, 1),
      },
    );
  }

  if (extraActions?.length) {
    actions.push({ type: 'separator' }, ...extraActions);
  }

  return (
    <UnifiedContextMenu actions={actions} data={item}>
      {children}
    </UnifiedContextMenu>
  );
}
